// @ts-nocheck
import { supabase } from '@/lib/supabase';

/**
 * Authorization helpers.
 *
 * `public.profiles.role` is a client-writable display column and must never be
 * used to gate access. Authorization in RLEvents lives in `user_roles` and
 * `organization_members`. Everything that decides what a user may see or do
 * should go through these helpers.
 */

/** rlevents_super_admin behaves as `admin` everywhere in the app. */
export const normalizeRole = (role?: string | null): string | null =>
  role === 'rlevents_super_admin' ? 'admin' : role ?? null;

/** All roles assigned to a user in `user_roles`, normalized. */
export async function fetchUserRoles(userId?: string | null): Promise<string[]> {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);
    if (error) {
      console.error('fetchUserRoles error:', error);
      return [];
    }
    return (data || []).map((r: any) => normalizeRole(r.role)).filter(Boolean) as string[];
  } catch (err) {
    console.error('fetchUserRoles threw:', err);
    return [];
  }
}

/** The user's primary (most recent) role, normalized. */
export async function fetchPrimaryRole(userId?: string | null): Promise<string | null> {
  if (!userId) return null;
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) {
      console.error('fetchPrimaryRole error:', error);
      return null;
    }
    return normalizeRole(data?.role);
  } catch (err) {
    console.error('fetchPrimaryRole threw:', err);
    return null;
  }
}

/** True when the user holds at least one of `roles` in `user_roles`. */
export async function userHasAnyRole(
  userId: string | null | undefined,
  roles: string[]
): Promise<boolean> {
  const assigned = await fetchUserRoles(userId);
  return assigned.some((r) => roles.includes(r));
}

/**
 * Replaces `profile.role` with the authoritative role from `user_roles`.
 * Used by the login path and by admin impersonation so an impersonated
 * session can never evaluate as more privileged than the real person.
 */
export async function withAuthoritativeRole<T extends Record<string, any>>(
  profile: T,
  userId?: string | null
): Promise<T> {
  if (!profile) return profile;
  const lookupId = userId || (profile as any).user_id;
  const role = await fetchPrimaryRole(lookupId);
  return { ...profile, role: role ?? null } as T;
}
